import * as React from "react";
import PlainDate from "@testout/testout-commerce/models/PlainDate";
import "./DateCtl.scss";

interface IDateProps {
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    value?: Date;
    onChange?: (value?: Date) => void;
}

interface IDateState {
    isOpen: boolean;
    hasFocus: boolean;
    calendarMonth: Date;
    inputValue: string;
    originalValue?: Date;
    caret: number;
}

export default class DateCtl extends React.PureComponent<IDateProps, IDateState> {

    constructor(props: IDateProps) {
        super(props);
        this.state = { ...DateCtl.getInitialState(props.value), isOpen: false, hasFocus: false, caret: 0 } as IDateState;
    }

    _controlRef = React.createRef<HTMLDivElement>();
    _valueRef = React.createRef<HTMLInputElement>();
    _needFocus = false;

    static getDerivedStateFromProps(props: IDateProps, state: IDateState) {
        // Only reinitialize state if the committed value has changed
        if (PlainDate.compare(props.value, state.originalValue) === 0) return null;
        return DateCtl.getInitialState(props.value);
    }

    static getInitialState(value?: Date): object {
        // Get the input and calendar state for committed value
        const calendarDate: Date = value ?? PlainDate.today ?? new Date();
        return {
            inputValue: value ? PlainDate.format(value, "MM/dd/yyyy") : "", 
            calendarMonth: new Date(calendarDate.getFullYear(), calendarDate.getMonth()),
            originalValue: value, 
        };
    }

    onValueFocus = (e: React.FocusEvent) => {
        // Open the calendar when the input FIRST receives focus
        if (this.props.disabled) return;
        if (!this.state.isOpen && !this.state.hasFocus)
            this.setState({ isOpen: true, hasFocus: true });
    }

    onValueClick = (e: React.MouseEvent<HTMLInputElement>) => {
        // Open the calendar when the input is clicked
        if (this.props.disabled) return;
        if (!this.state.isOpen)
            this.setState({ isOpen: true });
    }

    onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Get the changed value trimmed at caret
        let caret = e.currentTarget.selectionStart ?? 0;
        let value = e.currentTarget.value.substring(0, caret);

        // Determine the next valid value state
        // empty
        if (value === "") {/*nochange*/}

        // month
        else if (/^[0-1]$/.test(value)) {/*nochange*/}
        else if (/^[2-9]$/.test(value)) value = "0" + value + "/";
        else if (/^0[1-9]$/.test(value)) value = value + "/";
        else if (/^1[0-2]$/.test(value)) value = value + "/";
        else if (/^[1-9]\/$/.test(value)) value= "0" + value;

        // day
        else if (/^\d{2}\/[0-2]$/.test(value)) {/*nochange*/}
        else if (/^02\/3$/.test(value)) value = "02/03/20";
        else if (/^(01|03|04|05|06|07|08|09|10|11|12)\/3$/.test(value)) {/*nochange*/}
        else if (/^\d{2}\/[4-9]$/.test(value)) value = value.substr(0, 3) + "0" + value.substr(3) + "/20";
        else if (/^\d{2}\/0[1-9]$/.test(value)) value = value + "/20";
        else if (/^\d{2}\/[1-2][0-9]$/.test(value)) value = value + "/20";
        else if (/^(01|03|04|05|06|07|08|09|10|11|12)\/30$/.test(value)) value = value + "/20";
        else if (/^(01|03|05|07|08|10|12)\/31$/.test(value)) value = value + "/20";
        else if (/^\d{2}\/[1-9]\/$/.test(value)) value = value = value.substr(0, 3) + "0" + value.substr(3) + "20";

        // year
        else if (/^\d{2}\/\d{2}\/[0-9]{0,4}$/.test(value)) {/*nochange*/}
        else if (/^\d{2}\/\d{2}\/[0-9]{5}$/.test(value)) value = value.substr(0, 6) + value.substr(7);

        // invalid
        else value = this.state.inputValue;

        // Commit the changed input value if it's valid
        caret = value.length;
        if (value.length === 0 || value.length === 10)
            this.props.onChange?.(value.length === 0 ? undefined : PlainDate.parse(value.substr(6, 4) + "-" + value.substr(0, 2) + "-" + value.substr(3, 2)));

        // Update the calendar and input value
        const calendarMonth = this.getCalendarMonth(value);
        if (this.state.caret === caret && this.state.inputValue === value && PlainDate.compare(this.state.calendarMonth, calendarMonth) === 0)
            this.forceUpdate(); // We force the update so the caret position gets set
        else
            this.setState({ caret: caret, inputValue: value, calendarMonth: calendarMonth });
    }

    getCalendarMonth(value: string) {
        // Determine calender month start date using reasonable defaults when parts are missing
        const today = PlainDate.today;
        const y = parseInt(value.substr(6, 4)) || today.getFullYear();
        const m = parseInt(value.substr(0, 2)) || today.getMonth() + 1;
        return new Date(y, m - 1);
    }

    onValueKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.disabled) return;
        const { caret, inputValue, isOpen } = this.state;
        if (e.key === "Backspace") {
            if (inputValue) {
                // Ensure we skip the / if that's where the caret is positioned
                const count = inputValue.substr(inputValue.length - 1) === "/" ? 2 : 1;
                const value = inputValue.substr(0, inputValue.length - count);
                this.setState({ inputValue: value, caret: caret - count, calendarMonth: this.getCalendarMonth(value) });
            }
            e.preventDefault();
            e.stopPropagation();
        }
        else if (e.key === "Enter") {
            this.setState({ isOpen: !isOpen });
            e.preventDefault();
            e.stopPropagation();
        }
        else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            // Move the input value to previous or next day, month, or year
            const value = new Date(this.props.value ?? PlainDate.today);
            const offset = e.key === "ArrowUp" ? -1 : 1;
            if (e.ctrlKey && e.shiftKey) value.setFullYear(value.getFullYear() + offset);
            else if (e.ctrlKey) value.setMonth(value.getMonth() + offset);
            else value.setDate(value.getDate() + offset);

            // Commit the changed input value
            this.props.onChange?.(value);
            this.setState({ calendarMonth: new Date(value.getFullYear(), value.getMonth()) });
            e.preventDefault();
            e.stopPropagation();
        }
    }

    onPrevMonthClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.changeCalendarMonth(-1);
        e.stopPropagation();
        e.preventDefault();
    }

    onNextMonthClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.changeCalendarMonth(1);
        e.stopPropagation();
        e.preventDefault();
    }

    changeCalendarMonth(offset: number) {
        // Change the calendar to previous or next month
        const calendarMonth = new Date(this.state.calendarMonth);
        calendarMonth.setMonth(calendarMonth.getMonth() + offset);
        this.setState({ calendarMonth: calendarMonth });
        this._needFocus = true;
    }

    onDayClick = (day: Date, e: React.MouseEvent<HTMLTableCellElement>) => {
        this.selectCalendarDate(day);
        e.stopPropagation();
        e.preventDefault();
    }

    onTodayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.selectCalendarDate(PlainDate.today);
        e.stopPropagation();
        e.preventDefault();
    }

    onClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.selectCalendarDate(undefined);
        e.stopPropagation();
        e.preventDefault();
    }

    selectCalendarDate(value?: Date) {
        // Commit the specified date value and close the calendar
        this.props.onChange?.(value);
        this.setState({ isOpen: false });
        this._needFocus = true;
    }

    onDocumentFocusIn = (e: FocusEvent) => {
        // Close the calendar and reset the input value if it has not been committed 
        // when focus goes outside the input value and calendar
        const control = this._controlRef.current;
        if (control && !control.contains(e.target as Node))
            this.setState({ ...DateCtl.getInitialState(this.props.value), isOpen: false, hasFocus: false });
    }

    componentDidMount() {
        document.addEventListener("focusin", this.onDocumentFocusIn);
        document.addEventListener("click", this.onDocumentFocusIn);
    }

    componentDidUpdate() {
        if (this._needFocus) {
            // Reset focus back to hte input value when a button on the calendar has caused focus to be lost
            this._needFocus = false;
            const divValue = this._valueRef.current as HTMLDivElement;
            divValue.focus();
        }
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
        document.removeEventListener("click", this.onDocumentFocusIn);
    }

    render() {
        const { isOpen, inputValue, calendarMonth } = this.state;
        const { placeholder, disabled, value } = this.props;

        // Determine the selected date eliminated any time value
        const today = PlainDate.today;
        const selected = value ? new Date(value) : new Date();
        selected.setHours(0, 0, 0, 0);

        // Build the calendar array of weeks and days
        let day = new Date(calendarMonth);
        day.setDate(day.getDate() - day.getDay() - 1);
        let weeks: Date[][] = [];
        for (let w = 0; w < 6; w++) {
            weeks.push([]);
            for (let d = 0; d < 7; d++) 
                weeks[w].push(new Date(day.setDate(day.getDate() + 1)));
        }

        return <div className="date" ref={this._controlRef}>
            <input className={`value${disabled ? " disabled" : ""}`}
                type="text" 
                ref={this._valueRef} 
                placeholder={placeholder}
                value={inputValue}
                onChange={this.onValueChange} onKeyDown={this.onValueKeyDown}
                onFocus={this.onValueFocus}
                onClick={this.onValueClick} />
            {isOpen &&
                <div className="calendar">
                    <table>
                        <thead>
                            <tr>
                            <th><button tabIndex={-1} onClick={this.onPrevMonthClick} /></th>
                                <th colSpan={5}>{PlainDate.format(calendarMonth, "MMMM yyyy")}</th>
                                <th><button tabIndex={-1} onClick={this.onNextMonthClick} /></th>
                            </tr>
                            <tr><th>Su</th><th>Mo</th><th>Tu</th><th>We</th><th>Th</th><th>Fr</th><th>Sa</th></tr>
                        </thead>
                        <tbody>
                            {weeks.map((week: Date[], w: number) => <tr key={w}>
                                {week.map((day: Date, d: number) => <td key={d}
                                    onClick={(e: React.MouseEvent<HTMLTableDataCellElement>) => this.onDayClick(day, e)}
                                    className={day.getFullYear() !== calendarMonth.getFullYear() || day.getMonth() !== calendarMonth.getMonth()
                                        ? "other-month"
                                        : `${PlainDate.compare(day, today) === 0 ? "today" : ""} ${PlainDate.compare(day, value) === 0 ? "selected" : ""}`
                                        }>{day.getDate()}</td>)}
                            </tr>)}
                        </tbody>
                        <tfoot>
                            <tr><td colSpan={7}><div>
                            <button className="tertiary" tabIndex={-1} onClick={this.onTodayClick}>Today</button>
                            <button className="tertiary" tabIndex={-1} onClick={this.onClearClick}>Clear</button>
                            </div></td></tr>
                        </tfoot>
                    </table>
                </div>}
        </div>;
    }
}